import { useState } from 'react';
import { Chapter, Section } from '@/utils/bookStructure';

type StructureNavigatorProps = {
    chapters: Chapter[];
    sections: Section[];
    selectedChapterId: string;
    selectedSectionId: string;
    onSelectChapter: (chapterId: string) => void;
    onSelectSection: (sectionId: string) => void;
    onReorderChapters: (next: Chapter[]) => void;
    onReorderSections: (chapterId: string, next: Section[]) => void;
};

export default function StructureNavigator({
    chapters,
    sections,
    selectedChapterId,
    selectedSectionId,
    onSelectChapter,
    onSelectSection,
    onReorderChapters,
    onReorderSections,
}: StructureNavigatorProps) {
    const [dragChapterId, setDragChapterId] = useState<string | null>(null);
    const [dragSectionId, setDragSectionId] = useState<string | null>(null);

    const handleChapterDrop = (targetId: string) => {
        if (!dragChapterId || dragChapterId === targetId) return;
        const currentIndex = chapters.findIndex((chapter) => chapter.id === dragChapterId);
        const targetIndex = chapters.findIndex((chapter) => chapter.id === targetId);
        if (currentIndex === -1 || targetIndex === -1) return;
        const next = [...chapters];
        const [moved] = next.splice(currentIndex, 1);
        next.splice(targetIndex, 0, moved);
        onReorderChapters(next);
        setDragChapterId(null);
    };

    const handleSectionDrop = (chapterId: string, targetId: string) => {
        if (!dragSectionId || dragSectionId === targetId) return;
        const chapterSections = sections.filter((section) => section.chapterRef.id === chapterId);
        const currentIndex = chapterSections.findIndex((section) => section.id === dragSectionId);
        const targetIndex = chapterSections.findIndex((section) => section.id === targetId);
        if (currentIndex === -1 || targetIndex === -1) return;
        const next = [...chapterSections];
        const [moved] = next.splice(currentIndex, 1);
        next.splice(targetIndex, 0, moved);
        onReorderSections(chapterId, next);
        setDragSectionId(null);
    };

    return (
        <div className="space-y-4">
            <div className="text-sm font-semibold text-gray-700">構造ナビゲータ</div>
            <div className="space-y-3">
                {chapters.map((chapter) => (
                    <div key={chapter.id} className="space-y-2">
                        <button
                            type="button"
                            draggable
                            onDragStart={() => setDragChapterId(chapter.id)}
                            onDragOver={(event) => event.preventDefault()}
                            onDrop={() => handleChapterDrop(chapter.id)}
                            onClick={() => onSelectChapter(chapter.id)}
                            className={`w-full rounded-md border px-3 py-2 text-left text-sm font-semibold transition ${
                                selectedChapterId === chapter.id
                                    ? 'border-emerald-400 bg-emerald-50 text-emerald-700'
                                    : 'border-gray-100 bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                            {chapter.title}
                        </button>
                        <div className="space-y-1 pl-3">
                            {sections
                                .filter((section) => section.chapterRef.id === chapter.id)
                                .map((section) => (
                                    <button
                                        key={section.id}
                                        type="button"
                                        draggable
                                        onDragStart={() => setDragSectionId(section.id)}
                                        onDragOver={(event) => event.preventDefault()}
                                        onDrop={() => handleSectionDrop(chapter.id, section.id)}
                                        onClick={() => onSelectSection(section.id)}
                                        className={`w-full rounded-md border px-2 py-1 text-left text-xs transition ${
                                            selectedSectionId === section.id
                                                ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                                                : 'border-gray-100 bg-white text-gray-600 hover:bg-gray-50'
                                        }`}
                                    >
                                        {section.title}
                                    </button>
                                ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
